import { BiddingIntradayCsvRowStatus } from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Text, VStack } from '@enechain/ecloud-designsystem'

import {
  type BidCsvPreviewDataColumn,
  buildBidCsvPreviewColumns,
} from '~/components/bidding/common/bidCsvPreviewColumns'
import {
  type EnumBadgeSpec,
  EnumStatusBadge,
} from '~/components/common/EnumStatusBadge'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { CsvRowErrorMessages } from '~/pages/IntradayCsvImport/components/CsvRowErrorMessages'

/**
 * 差分プレビュー表の 1 行。 `columns` は spec §2 の列順
 * (受渡日 / コマ / 発注主体区分 / エリア / 売買 / 価格種別 / 価格 / 基準価格 /
 * 基準価格エリア / 演算子 / 調整値 / 数量 / 自動取消時刻 / JEPX自由記述欄 / メモ) に整形済みの
 * 文字列。
 * `messages` はエラー行の文言 (列の並び順に複数)。
 *
 * `orderPriceYenPerKwh` は 手数料自動考慮 ON の固定価格の行だけに入る、 算出済みの
 * 入札価格 (AC-BIN-004b)。 `columns` の「価格」列 (= ON では希望価格) とは別に持ち、
 * 価格セルの直下に補足表示して視覚的に区別する (spec §2「`価格 (円/kWh)` 列と
 * 視覚的に区別する（具体スタイルはデザイナー裁量）」)。 OFF・成行・基準価格の行は
 * undefined。
 */
export type IntradayCsvPreviewRow = {
  rowNumber: number
  status: BiddingIntradayCsvRowStatus
  columns: string[]
  messages: string[]
  orderPriceYenPerKwh?: string
}

/**
 * 時間前 CSV は spot / block と共有の `BiddingCsvRowStatus` (新規 / エラー の
 * 2 値) ではなく専用の `BiddingIntradayCsvRowStatus` を使う: エラーとは
 * 別に「対象外」区分 (GC済み / 発注主体区分未登録) を持つため
 * (spec intraday-csv-import.md §6、 AC-BCI-004b / AC-BCI-010)。
 * 対象外行はエラーと違い確定をブロックしないため `warning` (= 新規の `info` /
 * エラーの `negative` のどちらとも区別する中間の状態色)。
 */
const ROW_STATUS_BADGE: Record<BiddingIntradayCsvRowStatus, EnumBadgeSpec> = {
  [BiddingIntradayCsvRowStatus.UNSPECIFIED]: {
    status: 'neutral',
    labelKey: 'page:intradayCsvImport.rowStatus.unspecified',
  },
  [BiddingIntradayCsvRowStatus.CREATED]: {
    status: 'info',
    labelKey: 'page:intradayCsvImport.rowStatus.created',
  },
  [BiddingIntradayCsvRowStatus.EXCLUDED_GC_CLOSED]: {
    status: 'warning',
    labelKey: 'page:intradayCsvImport.rowStatus.excludedGcClosed',
  },
  [BiddingIntradayCsvRowStatus.EXCLUDED_ISSUER_AREA_UNREGISTERED]: {
    status: 'warning',
    labelKey: 'page:intradayCsvImport.rowStatus.excludedIssuerAreaUnregistered',
  },
  [BiddingIntradayCsvRowStatus.ERROR]: {
    status: 'negative',
    labelKey: 'page:intradayCsvImport.rowStatus.error',
  },
}

/**
 * 行ステータスのバッジ。 Step2 の差分プレビューと確定時のエラー表示
 * (`CsvConfirmRejection`) で同じ見た目にするため共有する (Des 回答 #8786)。
 */
export const IntradayCsvRowStatusBadge = ({
  status,
}: {
  status: BiddingIntradayCsvRowStatus
}): React.ReactElement => (
  <EnumStatusBadge map={ROW_STATUS_BADGE} value={status} />
)

/**
 * `dataColumns` 内の「価格」列の index (0 起算)。 手数料自動考慮 ON の補足表示
 * (`orderPriceYenPerKwh`) をこの列だけ特別扱いするために使う。
 */
const PRICE_COLUMN_INDEX = 6

/**
 * 「価格」列のセル。 手数料自動考慮 ON の固定価格の行 (`orderPriceYenPerKwh` が
 * 設定されている行) は、 CSV の値 (= 希望価格、 `columns` の price 列) の下に
 * 算出済みの入札価格を補足表示する (intraday-bidding.md AC-BIN-004b、
 * intraday-csv-import.md §2「`価格 (円/kWh)` 列と視覚的に区別する」)。
 * 装飾は既存の列の形 (数値セル + ミュートテキスト 1 行) にそろえ、 色・アイコン等の
 * 凝った装飾はしない。 コンポーネント本体の外に置くことで cell が「render の
 * たびに新しいコンポーネント型」にならないようにする (react/no-unstable-nested-components)。
 */
const PriceCell = ({
  row,
  t,
}: {
  row: IntradayCsvPreviewRow
  t: TFunction
}): React.ReactElement => (
  <VStack align="flex-end" gap={0}>
    <Text>{row.columns[PRICE_COLUMN_INDEX] ?? ''}</Text>
    {row.orderPriceYenPerKwh !== undefined && (
      <Text color="Text.midEmphasis" fontSize="sm">
        {t('page:intradayCsvImport.orderPriceSupplement', {
          price: row.orderPriceYenPerKwh,
        })}
      </Text>
    )}
  </VStack>
)

/**
 * プレビュー表の列定義。 列の骨格 (ステータス + 行番号 + 表示列 + エラー内容) は
 * スポット入札と共有し (`buildBidCsvPreviewColumns`)、 ここは行ステータスの区分と
 * セルの出し方だけを渡す。
 *
 * 揃えは 8080 の列揃え規約 (design `CLAUDE.md` / `css/components.css`) に従う:
 * 固定値列 (受渡日 / コマ / 発注主体区分 / エリア / 売買 / 価格種別 / 基準価格 /
 * 基準価格エリア / 演算子 / 自動取消時刻) は center、 数値 (行番号 / 価格 / 調整値 / 数量) は
 * right、 可変テキスト (JEPX自由記述欄 / メモ / エラー内容) は left。
 */
function buildPreviewColumns(
  t: TFunction,
): Array<MasterDataTableColumn<IntradayCsvPreviewRow>> {
  const dataColumns: BidCsvPreviewDataColumn[] = [
    { label: t('page:intradayCsvImport.columns.targetDate'), align: 'center' },
    { label: t('page:intradayCsvImport.columns.timeCode'), align: 'center' },
    {
      label: t('page:intradayCsvImport.columns.issuerCategory'),
      align: 'center',
    },
    { label: t('page:intradayCsvImport.columns.area'), align: 'center' },
    { label: t('page:intradayCsvImport.columns.side'), align: 'center' },
    { label: t('page:intradayCsvImport.columns.orderType'), align: 'center' },
    { label: t('page:intradayCsvImport.columns.price'), format: 'number' },
    {
      label: t('page:intradayCsvImport.columns.referencePrice'),
      align: 'center',
    },
    {
      label: t('page:intradayCsvImport.columns.referenceArea'),
      align: 'center',
    },
    { label: t('page:intradayCsvImport.columns.operator'), align: 'center' },
    { label: t('page:intradayCsvImport.columns.offset'), format: 'number' },
    { label: t('page:intradayCsvImport.columns.volume'), format: 'number' },
    { label: t('page:intradayCsvImport.columns.withdrawAt'), align: 'center' },
    { label: t('page:intradayCsvImport.columns.memoJepx'), align: 'left' },
    { label: t('page:intradayCsvImport.columns.memo'), align: 'left' },
  ]
  return buildBidCsvPreviewColumns<IntradayCsvPreviewRow>(t, {
    dataColumns,
    renderStatus: (row) => <IntradayCsvRowStatusBadge status={row.status} />,
    renderDataCell: (row, index) =>
      index === PRICE_COLUMN_INDEX ? (
        <PriceCell row={row} t={t} />
      ) : (
        (row.columns[index] ?? '')
      ),
    // 1 行に複数の違反があれば列の並び順に全部出す (spec の「列ごとに」表示)。
    renderMessage: (row) => <CsvRowErrorMessages messages={row.messages} />,
  })
}

type Props = {
  rows: IntradayCsvPreviewRow[]
}

/**
 * 時間前入札 CSV 取込の差分プレビュー表 (Step2)。
 * 既存札との突合は行わないため「変更」「据え置き」区分は持たず、 新規 / 対象外 2 種 /
 * エラー の 4 区分 (spec intraday-csv-import.md §6)。
 */
export const PreviewTable = ({ rows }: Props): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <MasterDataTable
      columns={buildPreviewColumns(t)}
      getRowKey={(row) => String(row.rowNumber)}
      rows={rows}
    />
  )
}
